import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { recurringApi, authApi } from '@/api/endpoints';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/dashboard/sessoes/recorrencias/$id')({
  component: RecorrenciaDetail,
});

const STATUS_LABEL: Record<string, string> = {
  PENDING: 'Aguardando',
  CONFIRMED: 'Confirmada',
  DONE: 'Concluída',
  DISPUTED: 'Em revisão',
  CANCELLED: 'Cancelada',
};
const STATUS_STYLE: Record<string, string> = {
  PENDING: 'text-amber-600',
  CONFIRMED: 'text-teal-600',
  DONE: 'text-slate-500',
  DISPUTED: 'text-orange-600',
  CANCELLED: 'text-red-500 line-through',
};

function RecorrenciaDetail() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: me } = useQuery({ queryKey: ['me'], queryFn: authApi.me });
  const isTherapist = !!me?.therapistProfile;

  const { data, isLoading } = useQuery({
    queryKey: ['recurring', id],
    queryFn: () => recurringApi.get(id),
  });

  const [extending, setExtending] = useState(false);
  const [extra, setExtra] = useState(4);

  const extend = useMutation({
    mutationFn: () => recurringApi.extend(id, { extraSessions: extra }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recurring', id] });
      qc.invalidateQueries({ queryKey: ['recurring'] });
      qc.invalidateQueries({ queryKey: ['sessions'] });
      setExtending(false);
    },
  });

  const cancel = useMutation({
    mutationFn: () => recurringApi.cancel(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recurring'] });
      qc.invalidateQueries({ queryKey: ['sessions'] });
      navigate({ to: '/dashboard/sessoes/recorrencias' });
    },
  });

  if (isLoading) {
    return (
      <div className="max-w-4xl">
        <div className="bg-white rounded-3xl p-12 animate-pulse h-48 border border-slate-100" />
      </div>
    );
  }
  if (!data) {
    return (
      <div className="max-w-4xl space-y-4">
        <Link to="/dashboard/sessoes/recorrencias" className="text-sm text-teal-600 hover:underline">
          ← Voltar
        </Link>
        <div className="bg-white rounded-3xl p-12 text-center border border-slate-100">
          <p className="text-slate-500">Série não encontrada.</p>
        </div>
      </div>
    );
  }

  const peer = isTherapist ? data.client : data.therapist;
  const peerName: string = peer?.user?.name ?? '—';
  const peerImage: string | null = peer?.avatarUrl ?? peer?.user?.image ?? null;
  const initial = peerName.charAt(0).toUpperCase();

  const freqLabel = data.frequency === 'WEEKLY' ? 'Semanal' : 'Mensal';
  const paymentLabel = data.paymentMode === 'UPFRONT_BATCH' ? 'Pré-pago' : 'Por sessão';

  const batchPayment =
    data.paymentMode === 'UPFRONT_BATCH'
      ? (data.payments ?? []).find((p: any) => p.status === 'PAID') ?? (data.payments ?? [])[0]
      : null;

  const isActive = data.status === 'ACTIVE';
  // R-10 — pacote já pago (UPFRONT_BATCH PAID) torna a série imutável
  const isLocked = data.paymentMode === 'UPFRONT_BATCH'
    && (data.payments ?? []).some((p: any) => p.status === 'PAID');

  return (
    <div className="max-w-4xl space-y-6">
      <div className="flex items-start gap-4">
        <Link
          to="/dashboard/sessoes/recorrencias"
          className="size-9 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500 transition shrink-0 mt-1"
          aria-label="Voltar"
        >
          ←
        </Link>

        {peerImage ? (
          <img src={peerImage} alt="" className="size-14 rounded-full object-cover shrink-0" />
        ) : (
          <div className="size-14 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center text-white font-bold text-xl shrink-0">
            {initial}
          </div>
        )}

        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-extrabold text-slate-900 truncate">{peerName}</h1>
          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
            <Pill>{freqLabel}</Pill>
            <Pill>{data.startTime}</Pill>
            <Pill>{data.durationMin} min</Pill>
            <Pill>{paymentLabel}</Pill>
          </div>
        </div>
      </div>

      {batchPayment && batchPayment.status === 'PAID' && (
        <div className="bg-teal-50 border border-teal-200 rounded-2xl p-5 flex items-start gap-4">
          <div className="size-9 rounded-full bg-teal-500 text-white flex items-center justify-center shrink-0">
            ✓
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-slate-800">Pacote pago</p>
            <p className="text-sm text-slate-600 mt-0.5">
              {Number(batchPayment.amount ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
              {batchPayment.paidAt && ` · Pago em ${new Date(batchPayment.paidAt).toLocaleDateString('pt-BR')}`}
            </p>
          </div>
        </div>
      )}

      {batchPayment && batchPayment.status !== 'PAID' && isTherapist && (
        <PendingBatchPaymentCard payment={batchPayment} clientName={peerName} />
      )}

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <ul className="divide-y divide-slate-100">
          {(data.sessions ?? []).map((s: any) => {
            const batchPaid = data.paymentMode === 'UPFRONT_BATCH'
              && (data.payments ?? []).some((p: any) => p.status === 'PAID');
            const isPaid = s.payment?.status === 'PAID' || batchPaid;
            return (
              <li key={s.id} className="p-4 flex items-center gap-3">
                <span className="text-slate-400 text-sm font-semibold w-5 tabular-nums shrink-0">
                  {s.recurringIndex}
                </span>
                <div
                  className={cn(
                    'size-7 rounded-full flex items-center justify-center text-white text-xs shrink-0',
                    s.status === 'DONE' ? 'bg-teal-500' :
                    s.status === 'CONFIRMED' ? 'bg-teal-300' :
                    s.status === 'CANCELLED' ? 'bg-red-300' :
                    'bg-slate-300',
                  )}
                >
                  {s.status === 'DONE' || s.status === 'CONFIRMED' ? '✓' : '·'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={cn('text-sm font-medium', s.status === 'CANCELLED' ? 'text-slate-400 line-through' : 'text-slate-700')}>
                    {new Date(s.scheduledAt).toLocaleString('pt-BR', {
                      weekday: 'short', day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC',
                    }).replace(',', '.,')} · {new Date(s.scheduledAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5 text-xs">
                    <span className={STATUS_STYLE[s.status] ?? 'text-slate-500'}>
                      {STATUS_LABEL[s.status] ?? s.status}
                    </span>
                    {isPaid && (
                      <span className="text-teal-600 inline-flex items-center gap-1">
                        <span aria-hidden>💳</span>
                        {data.paymentMode === 'UPFRONT_BATCH' ? 'pago (pacote)' : 'pago'}
                      </span>
                    )}
                  </div>
                </div>
                <Link
                  to="/dashboard/sessoes/$id"
                  params={{ id: s.id }}
                  className="text-sm text-teal-600 hover:underline shrink-0"
                >
                  Ver →
                </Link>
              </li>
            );
          })}
        </ul>
      </div>

      {isTherapist && isActive && !isLocked && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
          <p className="font-semibold text-slate-700 text-sm mb-3">Ações da série</p>
          {!extending ? (
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => setExtending(true)} className="text-sm">
                Estender série
              </Button>
              <Button
                variant="outline"
                className="text-sm text-red-600 hover:bg-red-50 hover:text-red-700 border-red-200"
                disabled={cancel.isPending}
                onClick={() => {
                  if (confirm('Cancelar todas as sessões futuras desta série? Esta ação não pode ser desfeita.')) {
                    cancel.mutate();
                  }
                }}
              >
                {cancel.isPending ? 'Cancelando...' : 'Cancelar série'}
              </Button>
            </div>
          ) : (
            <div className="flex flex-wrap items-end gap-3">
              <label className="block">
                <span className="text-xs font-medium text-slate-600">Sessões adicionais</span>
                <input
                  type="number"
                  min={1}
                  max={52}
                  value={extra}
                  onChange={(e) => setExtra(Math.max(1, Number(e.target.value) || 1))}
                  className="mt-1 block w-32 bg-white border border-slate-200 rounded-md h-10 px-3 text-sm"
                />
              </label>
              <Button
                onClick={() => extend.mutate()}
                disabled={extend.isPending || extra < 1}
                className="bg-teal-500 hover:bg-teal-600 text-white"
              >
                {extend.isPending ? 'Estendendo...' : 'Confirmar'}
              </Button>
              <Button variant="outline" onClick={() => setExtending(false)}>Voltar</Button>
            </div>
          )}
          {(extend.isError || cancel.isError) && (
            <p className="text-sm text-red-600 mt-2">Operação não pôde ser concluída.</p>
          )}
        </div>
      )}
    </div>
  );
}

function Pill({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-[11px] px-2.5 py-0.5 rounded-full bg-white text-slate-600 font-medium border border-slate-200">
      {children}
    </span>
  );
}

function PendingBatchPaymentCard({ payment, clientName }: { payment: any; clientName: string }) {
  const appUrl = typeof window !== 'undefined' ? window.location.origin : '';
  const link = `${appUrl}/pagamento/${payment.id}`;
  const amount = Number(payment.amount ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  const shareText = `Olá ${clientName}, segue o link para o pagamento das suas sessões (${amount}): ${link}`;
  const [copied, setCopied] = useState(false);

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="size-9 rounded-full bg-amber-400 text-white flex items-center justify-center shrink-0">⏳</div>
          <div>
            <p className="font-bold text-amber-900">Aguardando pagamento do cliente</p>
            <p className="text-sm text-amber-700 mt-0.5">
              {amount} · {payment.method ? `via ${payment.method}` : 'método ainda não escolhido'}
            </p>
          </div>
        </div>
      </div>

      <div>
        <p className="text-[10px] uppercase tracking-wide text-amber-700 font-semibold mb-1.5">
          Link para o cliente
        </p>
        <div className="flex items-stretch gap-2 flex-wrap">
          <div className="flex-1 min-w-[260px] bg-white border border-amber-200 rounded-md px-3 py-2 text-xs font-mono break-all flex items-center text-slate-700">
            {link}
          </div>
          <button
            onClick={async () => {
              await navigator.clipboard.writeText(link);
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            }}
            className="text-xs font-semibold text-amber-800 border border-amber-300 bg-white hover:bg-amber-50 px-3 py-2 rounded-md transition whitespace-nowrap"
          >
            {copied ? '✓ Copiado!' : 'Copiar link'}
          </button>
        </div>
        <p className="text-xs text-amber-700/80 mt-2">
          O cliente abre essa página e escolhe pagar com PIX ou cartão.
        </p>
      </div>

      <div className="flex gap-2 flex-wrap">
        <a
          href={`https://wa.me/?text=${encodeURIComponent(shareText)}`}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 bg-green-500 hover:bg-green-600 text-white text-xs font-semibold px-3 py-2 rounded-md transition"
        >
          <span aria-hidden>📱</span> WhatsApp
        </a>
        <a
          href={`mailto:?subject=${encodeURIComponent('Pagamento das suas sessões')}&body=${encodeURIComponent(shareText)}`}
          className="inline-flex items-center gap-1.5 bg-white border border-amber-300 text-amber-800 hover:bg-amber-50 text-xs font-semibold px-3 py-2 rounded-md transition"
        >
          <span aria-hidden>✉️</span> E-mail
        </a>
        <a
          href={`/pagamento/${payment.id}`}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-amber-800 hover:underline text-xs font-semibold px-2 py-2"
        >
          Visualizar página →
        </a>
      </div>
    </div>
  );
}
